import React, { useEffect, useState } from 'react';
import { useRouter } from 'next/navigation';
import { useSelector } from 'react-redux';

// Keyframes for zoom animation
const zoomAnimation = `
  @keyframes zoomInOutFront {
    0%, 100% {
      transform: translate(0, 0) scale(1);
    }
    50% {
      transform: translate(0, 0) scale(1.2);
    }
  }

  @media (min-width: 1280px) {
    @keyframes zoomInOutFront {
      0%, 100% {
        transform: translate(0, 0) scale(1);
      }
      50% {
        transform: translate(0, 0) scale(1.3);
      }
    }
  }

  @media (min-width: 1536px) {
    @keyframes zoomInOutFront {
      0%, 100% {
        transform: translate(0, 0) scale(1);
      }
      50% {
        transform: translate(0, 0) scale(1.4);
      }
    }
  }
`;

export default function LaptopHeroBanner() {
  const [headerHeight, setHeaderHeight] = useState('10rem');
  const router = useRouter();
  const { user, token, isAuthenticated } = useSelector(state => state.auth);

  useEffect(() => {
    const updateHeaderHeight = () => {
      if (window.innerWidth >= 768) {
        setHeaderHeight('10rem');
      } else if (window.innerWidth >= 640) {
        setHeaderHeight('9rem');
      } else {
        setHeaderHeight('8rem');
      }
    };

    updateHeaderHeight();
    window.addEventListener('resize', updateHeaderHeight);
    return () => window.removeEventListener('resize', updateHeaderHeight);
  }, []);

  const handleGetStarted = () => {
    // Check if user is authenticated
    if (!isAuthenticated && !token && !user) {
      router.push('/login');
      return;
    }

    // Check user type and redirect accordingly
    const userType = user?.user_type;
    if (userType === "Customer" || userType === "customer") {
      router.push('/customer-dashboard/job-post');
    } else if (userType === "Freelancer" || userType === "freelancer") {
      router.push('/freelancer-dashboard/jobs');
    } else {
      // Default to login if user type is unclear
      router.push('/login');
    }
  };

  return (
    <div className="relative w-full max-w-[1870px] mx-2 sm:mx-4 md:mx-8 my-4 md:my-8">
      <style>{zoomAnimation}</style>
      <div
        className="relative w-full overflow-hidden rounded-[16px] md:rounded-[32px]"
        style={{
          background: 'linear-gradient(to bottom right, #3A599C, #335191, #294685)',
          minHeight: 'calc(100vh - 12rem)'
        }}
      >
        {/* Grid Lines */}
        <div className="absolute inset-0 opacity-40">
          {/* Vertical lines */}
          <div className="absolute inset-0" style={{
            backgroundImage: 'linear-gradient(90deg, rgba(255,255,255,0.3) 1px, transparent 1px)',
            backgroundSize: '80px 100%'
          }}></div>
          {/* Horizontal lines */}
          <div className="absolute inset-0" style={{
            backgroundImage: 'linear-gradient(0deg, rgba(255,255,255,0.3) 1px, transparent 1px)',
            backgroundSize: '100% 80px'
          }}></div>
        </div>

        {/* Mobile Background Image */}
        <div className="lg:hidden absolute inset-0 flex items-center justify-center opacity-30">
          <div className="relative w-full h-full flex items-center justify-center">
            <img
              src="/images/cms-pages/lap3.webp"
              alt="Laptop Display Background"
              className="w-auto h-[80%] max-w-none object-contain"
            />
          </div>
        </div>

        {/* Content Section */}
        <div className="relative z-10 container mx-auto px-4 md:px-8 flex items-end lg:items-center justify-start" style={{ minHeight: 'calc(100vh - 12rem)' }}>
          <div className="w-full pb-12 md:pb-16 lg:pb-0">
            {/* Content - Bottom-left on mobile, center-left on large screens */}
            <div className="text-white space-y-3 md:space-y-4 lg:space-y-5 max-w-xl lg:max-w-lg">
              <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl xl:text-6xl leading-tight">
                Built for the <span className="text-white font-bold">Bold</span>
              </h1>

              <p className="text-xs sm:text-xs md:text-sm lg:text-sm xl:text-base text-white leading-relaxed">
                Hyrelancer was made for those who create, build, and believe in their craft.
              </p>

              <p className="text-xs sm:text-xs md:text-sm lg:text-sm xl:text-base text-white leading-relaxed">
                We’re bringing freelancing back to what it should be human, fair, and full of opportunity.
              </p>

              <button
                onClick={handleGetStarted}
                className="bg-white text-[#3A599C] px-5 sm:px-6 md:px-8 lg:px-10 xl:px-12 py-2 md:py-2.5 lg:py-3 rounded-full font-semibold text-xs sm:text-sm md:text-sm lg:text-base hover:bg-blue-50 transition-colors shadow-lg"
              >
                Get Started
              </button>
            </div>
          </div>
        </div>

        {/* Decorative gradient overlay */}
        <div className="absolute bottom-0 left-0 right-0 h-96 bg-gradient-to-t from-blue-800/20 to-transparent pointer-events-none"></div>
      </div>

      {/* lap3 - Front laptop without reflection - GOES OUTSIDE banner */}
      <div
        className="absolute pointer-events-none hidden lg:block"
        style={{
          right: 'clamp(1%, 3%, 5%)',
          top: 'clamp(1%, 2%, 4%)',
          width: 'clamp(600px, 50vw, 1000px)',
          height: 'auto',
          maxWidth: '55vw',
          zIndex: 5
        }}
      >
        <div
          className="relative w-full h-full"
          style={{
            animation: 'zoomInOutFront 5s ease-in-out infinite',
          }}
        >
          <img
            src="/images/cms-pages/lap3.webp"
            alt="Laptop Display Front"
            className="w-full h-auto object-contain drop-shadow-2xl"
          />
        </div>
      </div>
    </div>
  );
}